import {
    Avatar,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    InputBase,
    Chip,
    TablePagination,
} from "@mui/material";
import {
    Add as AddIcon,
    Search as SearchIcon,
    PeopleAlt as PeopleIcon,
    WhereToVote as ActiveIcon,
    DoNotDisturbOn as InactiveIcon,
} from "@mui/icons-material";
import {useEffect, useState, useMemo, useCallback, useRef} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import moment from "moment";

import {getTrainers, getTrainersPage} from "../services/TrainerService";
import NewTrainerDialog from "../componenets/NewTrainerDialog";
import SortableTableCell from "../componenets/SortableTableCell";
import {useCompany} from "../contexts/CompanyContext";
import {getErrorMessage} from "../enums/ErrorMessages";
import {useMessageContext} from "../contexts/MessageContext";
import {StatusChip, FilterDropdown} from "../componenets/ui";
import {ReactComponent as ActiveSvg} from "../assets/images/active.svg";
import {ReactComponent as InactiveSvg} from "../assets/images/inactive.svg";
import {colors, avatarPalette} from "../componenets/ui/colors";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {KPI} from "../componenets/dashboard/coach/KpiRow";
import {useAuth} from "../contexts/AuthContext";

/* ── Table cell presets (match CoachDashboard/Clients) ──────── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

const GENDER_OPTS = [
    {value: "ALL", label: "הכול"},
    {value: "MALE", label: "זכר", color: colors.info},
    {value: "FEMALE", label: "נקבה", color: "#BE185D"},
];

const STATUS_OPTS = [
    {value: "ALL", label: "הכול"},
    {value: "ACTIVE", label: "פעיל", color: colors.success},
    {value: "INACTIVE", label: "לא פעיל", color: colors.error},
];

/* ── Helpers ──────────────────────────────────────────────── */
const fmtDate = (ts) => {
    if (!ts) return "—";
    if (Array.isArray(ts)) return moment({year: ts[0], month: (ts[1] || 1) - 1, day: ts[2] || 1}).format("DD/MM/YY");
    if (typeof ts === "number") return moment.unix(ts).format("DD/MM/YY");
    return moment(ts).format("DD/MM/YY");
};

const TrainerAvatar = ({name}) => {
    const initials = name?.split(" ").map(w => w[0]).join("").slice(0, 2) || "?";
    const idx = name ? name.charCodeAt(0) % avatarPalette.length : 0;
    return (
        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
            <Avatar sx={{width: 36, height: 36, fontSize: "13px", fontWeight: 700, bgcolor: avatarPalette[idx], color: colors.white}}>
                {initials}
            </Avatar>
            <Typography sx={{fontSize: "13px", fontWeight: 600, color: colors.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                {name}
            </Typography>
        </Box>
    );
};

/* ═══════════════════════════════════════════════════════════
   Trainers page
   ═══════════════════════════════════════════════════════════ */
const Trainers = () => {
    const [trainers, setTrainers] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    // Company-wide counts for the KPI tiles, independent of the table's filters/paging.
    const [companyStats, setCompanyStats] = useState({total: 0, active: 0, inactive: 0});
    const {company} = useCompany();
    const [newTrainer, setNewTrainer] = useState(false);
    const {notificationHandler} = useMessageContext();
    // A company group admin has no creation capabilities, so the "new trainer" CTA is hidden for them.
    const {isCompanyGroupAdmin} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    // Filter states
    const [keyword, setKeyword] = useState("");
    const [gender, setGender] = useState("ALL");
    const [status, setStatus] = useState("ALL");

    // Server-side pagination
    const [pageId, setPageId] = useState(0);
    const [pageSize, setPageSize] = useState(25);

    // Sort state (mirrored to URL)
    const [sortBy, setSortBy] = useState(query.get("sortBy") || "name");
    const [sortDirection, setSortDirection] = useState(query.get("sortDirection") || "ASC");

    // Debounced search
    const [searchInput, setSearchInput] = useState("");
    const debounceRef = useRef(null);
    const handleSearch = useCallback((val) => {
        setSearchInput(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setKeyword(val), 300);
    }, []);

    const handleSort = useCallback((field, direction) => {
        setSortBy(field);
        setSortDirection(direction);
        const p = new URLSearchParams(location.search);
        p.set("sortBy", field);
        p.set("sortDirection", direction);
        navigate(`${location.pathname}?${p.toString()}`, {replace: true});
    }, [location.pathname, location.search, navigate]);

    // Reset to the first page whenever the filters or sort change, so we never land on an
    // out-of-range page (e.g. filtering down to 3 results while sitting on page 5).
    useEffect(() => {
        setPageId(0);
    }, [company, keyword, gender, status, sortBy, sortDirection]);

    // Table data — filtered, sorted and paged by the server.
    useEffect(() => {
        setLoading(true);
        const filters = {
            keyword: keyword || undefined,
            gender: gender === "ALL" ? undefined : gender,
            state: status === "ALL" ? undefined : status === "ACTIVE",
        };
        getTrainersPage(company?.id, sortBy, sortDirection, pageId, pageSize, filters)
            .then(r => {
                const page = r.data || {};
                setTrainers(page.content || []);
                setTotal(page.totalElements || 0);
                setLoading(false);
            }).catch(e => {
            notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company, sortBy, sortDirection, keyword, gender, status, pageId, pageSize]);

    // KPI tiles reflect the whole company, not the current page/filters — fetched once per company.
    useEffect(() => {
        getTrainers(company?.id)
            .then(r => {
                const all = r.data || [];
                const active = all.filter(tr => tr.enabled).length;
                setCompanyStats({total: all.length, active, inactive: all.length - active});
            })
            .catch(() => setCompanyStats({total: 0, active: 0, inactive: 0}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company]);

    const stats = companyStats;
    const hasActiveFilters = keyword !== "" || gender !== "ALL" || status !== "ALL";

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
            <NewTrainerDialog open={newTrainer} close={() => setNewTrainer(false)}/>

            {/* ─── Title row (sits on canvas, no white band) ───── */}
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
                <Box>
                    <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
                        מאמנים
                    </Typography>
                    <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול מאמנים</Typography>
                </Box>
                {!isCompanyGroupAdmin() && (
                <Box component="button" onClick={() => setNewTrainer(true)} sx={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    background: t.green, color: "#fff",
                    height: 34, px: "15px", borderRadius: t.rs,
                    fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
                    border: "none", cursor: "pointer", fontFamily: "inherit",
                    transition: "all .14s",
                    "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
                }}>
                    <AddIcon sx={{fontSize: 16}}/>
                    מאמן חדש
                </Box>
                )}
            </Box>

            {/* ─── KPI grid (3 tiles) ───────────────────────── */}
            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(3, 1fr)"}, gap: "14px", flexShrink: 0}}>
                <KPI
                    icon={<PeopleIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
                    value={stats.total.toLocaleString()} valueColor={t.green}
                    label="סה״כ מאמנים"
                    onClick={() => setStatus("ALL")}
                />
                <KPI
                    icon={<ActiveIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
                    badge="פעילים" badgeBg={t.greenLight} badgeColor={t.greenText}
                    value={stats.active.toLocaleString()} valueColor={t.green}
                    label="פעילים"
                    onClick={() => setStatus("ACTIVE")}
                />
                <KPI
                    icon={<InactiveIcon sx={{fontSize: 22}}/>} iconBg={t.redLight} iconColor={t.red}
                    badge="לא פעילים" badgeBg={t.redLight} badgeColor={t.redText}
                    value={stats.inactive.toLocaleString()} valueColor={t.red}
                    label="לא פעילים"
                    onClick={() => setStatus("INACTIVE")}
                />
            </Box>

            {/* ─── Table card (filters baked into top) ───────── */}
            <Box sx={{
                background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
                boxShadow: t.sh, overflow: "hidden",
                display: "flex", flexDirection: "column",
            }}>
                {/* Filter bar inside card */}
                <Box sx={{
                    display: "flex", alignItems: "center", gap: "7px",
                    padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
                    background: t.bg, flexWrap: "wrap", flexShrink: 0,
                }}>
                    {/* Count pill */}
                    <Box sx={{
                        display: "inline-flex", alignItems: "center", gap: "4px",
                        fontSize: "12px", fontWeight: 700, color: t.greenText,
                        background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
                        padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
                    }}>
                        <PeopleIcon sx={{fontSize: 14}}/>
                        {total} מאמנים
                    </Box>

                    {/* Search */}
                    <Box sx={{position: "relative"}}>
                        <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
                        <InputBase
                            value={searchInput}
                            onChange={e => handleSearch(e.target.value)}
                            placeholder="חיפוש לפי שם..."
                            sx={{
                                width: 200,
                                padding: "6px 10px 6px 30px",
                                border: `1.5px solid ${t.border}`,
                                borderRadius: t.rs,
                                background: t.white,
                                color: t.text,
                                fontSize: "12px",
                                "& input": {padding: 0},
                                "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                            }}
                        />
                    </Box>

                    {/* Status + Gender — next to search */}
                    <FilterDropdown label="סטטוס" value={status} onChange={setStatus} options={STATUS_OPTS} minWidth={120}/>
                    <FilterDropdown label="מגדר" value={gender} onChange={setGender} options={GENDER_OPTS} minWidth={90}/>

                    <Box sx={{flex: 1}}/>
                </Box>

                {/* Body */}
                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
                        <CircularProgress size={32} sx={{color: t.green}}/>
                    </Box>
                ) : !trainers.length ? (
                    <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
                        <Typography sx={{color: t.t3, fontSize: "13px", fontWeight: 500}}>
                            {hasActiveFilters ? "אין מאמנים תואמים" : "אין מאמנים"}
                        </Typography>
                        <Typography sx={{color: t.t3, fontSize: "12px"}}>
                            {hasActiveFilters ? "נסה לשנות את הסינון או החיפוש" : "הוסף מאמן חדש להתחיל"}
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{overflowY: "auto", maxHeight: 540}}>
                        <Table stickyHeader sx={{tableLayout: "fixed"}}>
                            <TableHead>
                                <TableRow>
                                    <SortableTableCell field="name" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort}
                                                       sx={{...th, textAlign: "right", width: 200}}>שם מלא</SortableTableCell>
                                    <SortableTableCell field="gender" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort}
                                                       sx={{...th, textAlign: "center", width: 75, display: {xs: "none", md: "table-cell"}}}>מגדר</SortableTableCell>
                                    <SortableTableCell field="numberOfClients" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort}
                                                       sx={{...th, textAlign: "center", width: 90}}>לקוחות</SortableTableCell>
                                    <SortableTableCell field="createdDate" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort}
                                                       sx={{...th, textAlign: "center", width: 100, display: {xs: "none", md: "table-cell"}}}>תאריך הצטרפות</SortableTableCell>
                                    <SortableTableCell field="enabled" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort}
                                                       sx={{...th, textAlign: "center", width: 80}}>סטטוס</SortableTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {trainers.map(trainer => (
                                    <TableRow key={trainer.id} onClick={() => navigate(`/app/trainers/${trainer.id}`)}
                                              sx={{
                                                  cursor: "pointer",
                                                  "&:hover td": {background: t.greenLight},
                                                  transition: "background .12s ease",
                                              }}>
                                        <TableCell sx={{...td, textAlign: "right", overflow: "hidden"}}>
                                            <TrainerAvatar name={trainer.name}/>
                                        </TableCell>
                                        <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                            {trainer.gender === "MALE" ? (
                                                <Chip label="זכר" size="small" sx={{fontSize: "11px", fontWeight: 600, height: 22, bgcolor: `${colors.info}15`, color: colors.info, border: `1px solid ${colors.info}30`}}/>
                                            ) : trainer.gender === "FEMALE" ? (
                                                <Chip label="נקבה" size="small" sx={{fontSize: "11px", fontWeight: 600, height: 22, bgcolor: "#BE185D15", color: "#BE185D", border: "1px solid #BE185D30"}}/>
                                            ) : (
                                                <Typography sx={{fontSize: "12px", color: t.t3}}>—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{...td, textAlign: "center"}}>
                                            <Chip label={trainer.numberOfClients ?? 0} size="small" sx={{fontSize: "12px", fontWeight: 600, height: 22, minWidth: 32, bgcolor: t.greenLight, color: t.greenText}}/>
                                        </TableCell>
                                        <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                            <Typography sx={{fontSize: "12px", color: t.t2}}>
                                                {fmtDate(trainer.createdDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{...td, textAlign: "center"}}>
                                            {trainer.enabled
                                                ? <StatusChip label="פעיל" variant="success" icon={<ActiveSvg style={{width: 11, height: 11}}/>} size="small"/>
                                                : <StatusChip label="לא פעיל" variant="error" icon={<InactiveSvg style={{width: 11, height: 11}}/>} size="small"/>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Pagination — driven by the server's totalElements (filtered total across all pages) */}
                {!loading && total > 0 && (
                    <TablePagination
                        component="div"
                        count={total}
                        page={pageId}
                        onPageChange={(e, newPage) => setPageId(newPage)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => {
                            setPageSize(parseInt(e.target.value, 10));
                            setPageId(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="שורות בעמוד:"
                        labelDisplayedRows={({from, to, count}) => `${from}–${to} מתוך ${count}`}
                        sx={{borderTop: `1px solid ${t.border}`, flexShrink: 0}}
                    />
                )}
            </Box>
        </Box>
    );
};

export default Trainers;
